import { AppState, Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoadingIcon } from "../../components/ui/LoadingIcon";
import { isSessionExpired } from "./AppOverlayController";

declare global {
  interface Window {
    Cypress?: boolean;
    runtime: Record<string, string>;
  }
}

const CLEAR_CONNECTION_KEYWORD = "none";

const Auth0Initializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isAuthenticated, loginWithRedirect, user } = useAuth0();
  const hasBeenAuthenticated = useRef(false);
  // eslint-disable-next-line react-hooks/refs -- intentional sticky flag: once authenticated, never show the login redirect again even on transient re-renders where isAuthenticated briefly flips false
  if (isAuthenticated) hasBeenAuthenticated.current = true;

  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({ id: user.sub, email: user.email });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user.sub is the stable identity; full user object reference is not stable across Auth0 renders
  }, [isAuthenticated, user?.sub]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasBeenAuthenticated.current) {
      const returnTo = window.location.hash.slice(1) || "/";
      void loginWithRedirect({ appState: { returnTo } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Defer to the session-expired overlay instead of covering it with this loader.
  // eslint-disable-next-line react-hooks/refs -- reading the sticky flag during render to avoid a flash of the loading screen when isAuthenticated briefly flips false after a prior successful auth
  if ((isLoading || (!isAuthenticated && !hasBeenAuthenticated.current)) && !isSessionExpired()) {
    return <LoadingIcon boxWidth="100vw" boxHeight="100vh" />;
  }

  return <>{children}</>;
};

interface Props {
  children: React.ReactNode;
}

export const AuthShell: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlParamsConnection = searchParams.get("connection");
  const storedConnection = localStorage.getItem("connection");
  let connection = urlParamsConnection ?? storedConnection ?? undefined;
  connection = connection === CLEAR_CONNECTION_KEYWORD ? undefined : connection;

  useEffect(() => {
    if (urlParamsConnection !== null && urlParamsConnection !== CLEAR_CONNECTION_KEYWORD) {
      localStorage.setItem("connection", urlParamsConnection);
    }
    if (urlParamsConnection === CLEAR_CONNECTION_KEYWORD) {
      localStorage.removeItem("connection");
    }
  }, [urlParamsConnection]);

  const onRedirectCallback = (appState?: AppState): void => {
    void navigate(appState?.returnTo ?? "/");
  };

  const domain = window.runtime.VITE_AUTH0_DOMAIN;
  const clientId = window.runtime.VITE_AUTH0_CLIENT_ID;
  const audience = window.runtime.VITE_AUTH0_AUDIENCE;
  const redirectUri = `${window.location.origin}/#/callback`;

  if (!(domain && clientId && redirectUri && audience)) {
    return null;
  }

  const cacheLocation: "memory" | "localstorage" =
    typeof window !== "undefined" && window.Cypress ? "localstorage" : "memory";

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{ redirect_uri: redirectUri, audience, connection }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation={cacheLocation}
    >
      <Auth0Initializer>{children}</Auth0Initializer>
    </Auth0Provider>
  );
};
